import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Brain, FileText, Search } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CostLimitBanner } from '@/components/ui/CostLimitBanner';
import { Drawer } from '@/components/ui/Drawer';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { TokenStatsBadge } from '@/components/ui/TokenStatsBadge';
import { ValidationPanel } from '@/components/ui/ValidationPanel';
import { YamlViewer } from '@/components/ui/YamlViewer';
import { formatDateTime } from '@/lib/format';
import { useAiSession, useAiSessions, type AiSession } from '@/hooks/useAi';

/**
 * /ai/sessions - KI-Sessions-Audit (Sprint 6, WT-606).
 *
 * Tabelle aller persistierten KI-Anfragen mit Validation-Status, Kosten und
 * Klick-zu-Detail-Drawer (pseudonymisiertes Input + Output + Validation).
 *
 * Filter: Status, Customer (sobald Multi-Tenant), Date-Range.
 */

export const Route = createFileRoute('/ai/sessions')({
  component: AiSessionsPage,
});

const STATUS_OPTIONS: Array<{ value: AiSession['status'] | ''; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'success', label: 'Vorschlag generiert' },
  { value: 'accepted', label: 'Akzeptiert' },
  { value: 'rejected', label: 'Verworfen' },
  { value: 'failed', label: 'Fehler' },
];

function sessionStatusFilterValue(status: AiSession['status']): string {
  switch (status) {
    case 'accepted':
      return 'akzeptiert';
    case 'success':
      return 'generiert';
    case 'rejected':
      return 'verworfen';
    case 'failed':
      return 'fehler';
  }
}

function tokenCostLabel(s: AiSession): string {
  const usage = s.usage;
  const total = usage.input_tokens + usage.cache_read_tokens + usage.output_tokens;
  const usd =
    usage.cost_usd < 0.001
      ? '<$0.001'
      : usage.cost_usd < 1
        ? `$${usage.cost_usd.toFixed(4)}`
        : `$${usage.cost_usd.toFixed(2)}`;
  const tok = total < 1000 ? `${total}` : `${(total / 1000).toFixed(1)}K`;
  return `${usd} · ${tok} Tok`;
}

function validationFilterValue(summary: AiSession['validation_summary']): string {
  return summary.schema_valid && summary.allowlist_valid && summary.syntax_valid
    ? 'ok'
    : 'fehler';
}

const KI_SESSION_COLUMNS: ReadonlyArray<Column<AiSession>> = [
  {
    id: 'datum',
    header: 'Datum',
    className: 'text-xs',
    filterValue: (s) => {
      const label = formatDateTime(s.created_at);
      return label === '–' ? '' : label;
    },
    cell: (s) => formatDateTime(s.created_at),
  },
  {
    id: 'system-kunde',
    header: 'System / Kunde',
    filterValue: (s) => [s.system_pseudonym ?? '', s.customer_name ?? ''].join(' '),
    cell: (s) => (
      // Backend liefert (noch) kein system_pseudonym/customer_name
      // im Listen-Endpoint -> klarer Platzhalter statt leer.
      <div className="flex flex-col">
        <span className="font-mono text-xs">{s.system_pseudonym ?? '–'}</span>
        <span className="text-xs text-muted">{s.customer_name ?? '–'}</span>
      </div>
    ),
  },
  {
    id: 'intent',
    header: 'Intent',
    filter: 'select',
    filterValue: (s) => {
      if (s.intent_truncated) return s.intent_truncated;
      return s.purpose ? `Zweck: ${s.purpose}` : '';
    },
    cell: (s) =>
      s.intent_truncated ? (
        <span className="line-clamp-2 max-w-md text-sm">{s.intent_truncated}</span>
      ) : (
        <div className="flex flex-col">
          <span className="text-sm text-muted">–</span>
          {s.purpose && (
            <span className="text-xs text-muted">Zweck: {s.purpose}</span>
          )}
        </div>
      ),
  },
  {
    id: 'validation',
    header: 'Validation',
    filter: 'select',
    filterValue: (s) => validationFilterValue(s.validation_summary),
    cell: (s) => <ValidationDots summary={s.validation_summary} />,
  },
  {
    id: 'tokens-cost',
    header: 'Tokens / Cost',
    headerClassName: 'text-right',
    className: 'text-right',
    filterValue: (s) => tokenCostLabel(s),
    cell: (s) => <TokenStatsBadge usage={s.usage} compact />,
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: (s) => sessionStatusFilterValue(s.status),
    cell: (s) => <SessionStatusBadge status={s.status} />,
  },
];

function AiSessionsPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<AiSession['status'] | ''>('');
  const [search, setSearch] = useState('');
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);

  const query = useAiSessions(
    statusFilter ? { status: statusFilter, limit: 100 } : { limit: 100 }
  );

  const sessions = useMemo(() => query.data?.data ?? [], [query.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) =>
      [s.intent_truncated, s.purpose ?? '', s.customer_name ?? '', s.system_pseudonym ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [sessions, search]);

  const totals = useMemo(() => {
    let cost = 0;
    let inputTok = 0;
    let outputTok = 0;
    for (const s of sessions) {
      cost += s.usage.cost_usd;
      inputTok += s.usage.input_tokens + s.usage.cache_read_tokens;
      outputTok += s.usage.output_tokens;
    }
    return { cost, inputTok, outputTok };
  }, [sessions]);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="size-5 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-semibold">KI-Sessions Audit</h1>
          </div>
          <p className="text-muted">
            Alle KI-Anfragen mit pseudonymisiertem Input, Validation-Status und
            Token-Kosten. Append-only. Kunde/System/Intent sind in der Liste erst
            nach einer Backend-Erweiterung verfügbar (bis dahin &laquo;&ndash;&raquo;).
          </p>
        </div>
        <Link to="/ai/suggest">
          <Button leftIcon={<Brain className="size-4" />}>Neuer KI-Vorschlag</Button>
        </Link>
      </header>

      <CostLimitBanner />

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche (Intent / Customer / System) ..."
              aria-label="KI-Sessions durchsuchen"
              className="rounded-md border border-border bg-surface px-8 py-2 text-sm placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="ai-sessions-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AiSession['status'] | '')}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Status-Filter"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="ml-auto inline-flex flex-wrap items-center gap-2 text-xs text-muted">
            <span>{filtered.length} Sessions</span>
            <span>·</span>
            <span className="font-mono">
              ${totals.cost.toFixed(4)} gesamt ({(totals.inputTok / 1000).toFixed(1)}K in,{' '}
              {(totals.outputTok / 1000).toFixed(1)}K out)
            </span>
          </span>
        </div>

        {query.isLoading && <SkeletonRows rows={4} cols={5} />}
        {query.isError && <ErrorAlert error={query.error} />}
        {!query.isLoading && !query.isError && filtered.length === 0 && (
          <EmptyState
            icon={<Brain className="size-8" />}
            title="Noch keine KI-Sessions"
            description={
              search || statusFilter
                ? 'Keine Treffer für den Filter.'
                : 'Sobald du einen KI-Vorschlag generierst, erscheint die Session hier mit pseudonymisiertem Input.'
            }
            actions={
              <Link to="/ai/suggest">
                <Button leftIcon={<Brain className="size-4" />}>
                  Ersten Vorschlag generieren
                </Button>
              </Link>
            }
          />
        )}

        {filtered.length > 0 && (
          <DataTable
            caption="KI-Sessions Audit"
            columns={KI_SESSION_COLUMNS}
            rows={filtered}
            rowKey={(s) => s.id}
            storageKey="ki-sessions"
            onRowClick={(s) => setOpenSessionId(s.id)}
            rowTestId={(s) => `ai-session-row-${s.id}`}
          />
        )}
      </Card>

      <SessionDrawer
        sessionId={openSessionId}
        onClose={() => setOpenSessionId(null)}
      />
    </div>
  );
}

function ValidationDots({
  summary,
}: {
  summary: AiSession['validation_summary'];
}): JSX.Element {
  return (
    <span className="inline-flex gap-1" aria-label="Validation Summary">
      <Dot ok={summary.schema_valid} label="Schema" />
      <Dot ok={summary.allowlist_valid} label="Allowlist" />
      <Dot ok={summary.syntax_valid} label="Syntax" />
    </span>
  );
}

function Dot({ ok, label }: { ok: boolean; label: string }): JSX.Element {
  return (
    <span
      title={`${label}: ${ok ? 'ok' : 'fehler'}`}
      className={[
        'inline-block size-2 rounded-full',
        ok ? 'bg-success' : 'bg-danger',
      ].join(' ')}
      aria-label={`${label}: ${ok ? 'ok' : 'fehler'}`}
    />
  );
}

function SessionStatusBadge({
  status,
}: {
  status: AiSession['status'];
}): JSX.Element {
  switch (status) {
    case 'accepted':
      return <Badge tone="success">akzeptiert</Badge>;
    case 'success':
      return <Badge tone="primary">generiert</Badge>;
    case 'rejected':
      return <Badge tone="neutral">verworfen</Badge>;
    case 'failed':
      return <Badge tone="danger">fehler</Badge>;
    default:
      return <Badge tone="neutral">{status}</Badge>;
  }
}

function SessionDrawer({
  sessionId,
  onClose,
}: {
  sessionId: string | null;
  onClose: () => void;
}): JSX.Element {
  const detail = useAiSession(sessionId ?? undefined);

  return (
    <Drawer
      open={Boolean(sessionId)}
      onClose={onClose}
      title="KI-Session Detail"
      description={
        sessionId ? (
          <span className="font-mono text-xs">{sessionId}</span>
        ) : null
      }
      widthClass="max-w-3xl"
    >
      {detail.isLoading && <SkeletonRows rows={4} cols={2} />}
      {detail.isError && <ErrorAlert error={detail.error} />}
      {detail.data && (
        <div className="flex flex-col gap-4">
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <FileText className="size-4" /> Input (pseudonymisiert)
            </h3>
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-surface-2/40 p-3 text-xs">
              {detail.data.intent_full}
            </pre>
            {detail.data.system_pseudonym && (
              <p className="mt-1 text-xs text-muted">
                System: <code className="font-mono">{detail.data.system_pseudonym}</code>
                {detail.data.customer_name && ` (Kunde: ${detail.data.customer_name})`}
              </p>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Validation</h3>
            <ValidationPanel validation={detail.data.validation} />
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Generated Playbook</h3>
            <YamlViewer source={detail.data.playbook_yaml} />
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Token-Stats</h3>
            <TokenStatsBadge usage={detail.data.usage} />
          </section>
        </div>
      )}
    </Drawer>
  );
}
